import { useEffect, useMemo, useState, type FormEvent } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Drawer } from "../Drawer";
import { Icon } from "../ui/Icon";
import { SearchSelect, type SSGroup, type SSOption } from "../ui/SearchSelect";
import { TesisPickerField, useTesisPicker, resolveTesisId } from "../ui/TesisPickerField";
import { trpc } from "../../lib/trpc";

type ProducibleKind = "semi" | "finished";
type ItemKind = "stock" | "semi" | "finished";
type RecipeMode = "percentage" | "absolute";

interface ItemRow {
  consumedKind: ItemKind;
  consumedId: string;
  qty: string;
  uomId: string;
  scrapPct: string;
  consumedInConversion: boolean;
}

// Yan ürün satırı (co-production): üretilen ek çıktı. Miktar reçete batch'i için (yan ürünün taban biriminde).
interface BypRow {
  itemKind: ProducibleKind;
  itemId: string;
  qty: string;
}

interface RecipeEditorProps {
  open: boolean;
  /** Edit modu: mevcut reçete id; yoksa create */
  recipeId?: string | null;
  /** Kopyala modu: kaynak reçete id — içeriği yeni taslağa ön-doldurulur, üretilen ürünü kullanıcı seçer. */
  copyFromId?: string | null;
  onClose: () => void;
}

const emptyRow = (uomId: string): ItemRow => ({ consumedKind: "stock", consumedId: "", qty: "", uomId, scrapPct: "0", consumedInConversion: false });

const CUR_SYMBOL: Record<string, string> = { TRY: "₺", USD: "$", EUR: "€", GBP: "£" };
function fmtMoney(n: number): string {
  return new Intl.NumberFormat("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);
}

export function RecipeEditorDrawer({ open, recipeId, copyFromId, onClose }: RecipeEditorProps) {
  const isEdit = !!recipeId;
  const isCopy = !!copyFromId && !isEdit;
  const qc = useQueryClient();

  const [producedKind, setProducedKind] = useState<ProducibleKind>("finished");
  const [producedId, setProducedId] = useState("");
  const [mode, setMode] = useState<RecipeMode>("absolute");
  const [batchSize, setBatchSize] = useState("");
  const [batchUomId, setBatchUomId] = useState("");
  const [yieldPct, setYieldPct] = useState("100");
  const [notes, setNotes] = useState("");
  const [productionCenterId, setProductionCenterId] = useState("");
  const [rows, setRows] = useState<ItemRow[]>([]);
  const [byproducts, setByproducts] = useState<BypRow[]>([]);
  const [submitErr, setSubmitErr] = useState<string | null>(null);
  const tp = useTesisPicker(!isEdit);
  const [tesisPick, setTesisPick] = useState("");

  // Referans veri
  const uomQ = trpc.uom.list.useQuery(undefined, { staleTime: 5 * 60_000 });
  const produciblesQ = trpc.recipe.producibles.useQuery(undefined, { enabled: open, staleTime: 60_000 });
  // Malzeme seçici: TÜM aktif kalemler (v_items) — pagination YOK (eski cap'li list 100'de kesiyordu).
  const itemOptsQ = trpc.inventory.itemOptions.useQuery(undefined, { enabled: open, staleTime: 60_000 });
  // Üretim merkezleri (Location type=uretim_merkezi, aktif) — reçete merkez seçici için.
  const locsQ = trpc.location.list.useQuery(undefined, { enabled: open, staleTime: 60_000 });

  const existingQ = trpc.recipe.get.useQuery({ id: recipeId! }, { enabled: open && isEdit });
  const sourceQ = trpc.recipe.get.useQuery({ id: copyFromId! }, { enabled: open && isCopy });

  const createMut = trpc.recipe.create.useMutation();
  const updateMut = trpc.recipe.update.useMutation();
  const isPending = createMut.isPending || updateMut.isPending;

  const kgUom = uomQ.data?.find((u) => u.code === "kg");

  // Form'u doldur
  useEffect(() => {
    if (!open) return;
    setSubmitErr(null);
    const mapItems = (items: Array<{ consumedKind: string; consumedId: string; qty: number; uomId: string; scrapPct: number; consumedInConversion: boolean }>) =>
      items.map((i) => ({
        consumedKind: i.consumedKind as ItemKind,
        consumedId: i.consumedId,
        qty: String(i.qty),
        uomId: i.uomId,
        scrapPct: String(i.scrapPct),
        consumedInConversion: i.consumedInConversion,
      }));
    const mapByps = (bps: Array<{ itemKind: string; itemId: string; qty: number }>) =>
      bps.map((b) => ({ itemKind: b.itemKind as ProducibleKind, itemId: b.itemId, qty: String(b.qty) }));
    if (isEdit && existingQ.data) {
      const r = existingQ.data;
      setProducedKind(r.producedKind as ProducibleKind);
      setProducedId(r.producedId);
      setMode(r.mode as RecipeMode);
      setBatchSize(String(r.batchSize));
      setBatchUomId(r.batchUomId);
      setYieldPct(String(r.expectedYieldPct));
      setNotes(r.notes ?? "");
      setProductionCenterId(r.productionCenterId ?? "");
      setRows(mapItems(r.items));
      setByproducts(mapByps(r.byproducts));
    } else if (isCopy) {
      // Kaynak reçetenin içeriğini ön-doldur; üretilen ürünü kullanıcı seçer (hedef).
      if (sourceQ.data) {
        const r = sourceQ.data;
        setProducedKind(r.producedKind as ProducibleKind);
        setProducedId(""); // hedef ürün seçilecek
        setMode(r.mode as RecipeMode);
        setBatchSize(String(r.batchSize));
        setBatchUomId(r.batchUomId);
        setYieldPct(String(r.expectedYieldPct));
        setNotes(r.notes ?? "");
        setProductionCenterId(r.productionCenterId ?? "");
        setRows(mapItems(r.items));
        setByproducts(mapByps(r.byproducts));
      } else {
        // kaynak yükleniyor — eski içeriği gösterme
        setProducedId("");
        setRows([]);
        setByproducts([]);
      }
    } else {
      setProducedKind("finished");
      setProducedId("");
      setMode("absolute");
      setBatchSize("");
      setBatchUomId(kgUom?.id ?? "");
      setYieldPct("100");
      setNotes("");
      setProductionCenterId("");
      setRows([emptyRow(kgUom?.id ?? "")]);
      setByproducts([]);
    }
  }, [open, isEdit, isCopy, existingQ.data, sourceQ.data, kgUom?.id]);
  // Tesis seçimi reset (yalnız create/copy'de görünür).
  useEffect(() => { if (open && !isEdit) setTesisPick(""); }, [open, isEdit]);

  // Tüketilebilecek tüm item'lar (stock + semi + finished), gruplu
  const itemOptions = useMemo(() => {
    const all = (itemOptsQ.data ?? []) as Array<{ id: string; kind: string; code: string; name: string; baseUomId?: string | null }>;
    return {
      stock: all.filter((r) => r.kind === "stock"),
      semi: all.filter((r) => r.kind === "semi"),
      finished: all.filter((r) => r.kind === "finished"),
    };
  }, [itemOptsQ.data]);

  /**
   * Kalem → KART TABAN BİRİMİ. Kalemin birimi karttır; reçete satırındaki birim yalnız bir
   * veri-giriş alanıdır ve yanlış girilebilir (canlıda 477 satırın 32'si böyle: kart "ad"
   * derken satıra "kg" yazılmış → MRP raporunda "7.500 kg koli" görünüyordu).
   */
  const kartBirimi = useMemo(() => {
    const m = new Map<string, string>();
    for (const o of (itemOptsQ.data ?? []) as Array<{ id: string; baseUomId?: string | null }>) {
      if (o.baseUomId) m.set(o.id, o.baseUomId);
    }
    return m;
  }, [itemOptsQ.data]);

  // Aranabilir malzeme seçici için gruplu seçenekler (ad = etiket, kod = mono tail — ikisi de aranır).
  const ingredientGroups = useMemo<SSGroup[]>(() => {
    const groups: SSGroup[] = [];
    if (itemOptions.stock.length)
      groups.push({ group: "Hammadde / Sarf", items: itemOptions.stock.map((o) => ({ value: `stock:${o.id}`, label: o.name, tail: o.code })) });
    if (itemOptions.semi.length)
      groups.push({ group: "Yarı Mamul", items: itemOptions.semi.map((o) => ({ value: `semi:${o.id}`, label: o.name, tail: o.code })) });
    if (itemOptions.finished.length)
      groups.push({ group: "Mamul", items: itemOptions.finished.map((o) => ({ value: `finished:${o.id}`, label: o.name, tail: o.code })) });
    return groups;
  }, [itemOptions]);

  // Aranabilir üretilen ürün seçici — seçili türe (semi/finished) göre filtrelenir.
  const produciblesOptions = useMemo<SSOption[]>(
    () => (produciblesQ.data ?? [])
      .filter((p) => p.kind === producedKind)
      .map((p) => ({ value: p.id, label: p.name, tail: p.code })),
    [produciblesQ.data, producedKind],
  );

  // Yan ürün seçenekleri: üretilebilir (semi/finished) — üretilen ürünün kendisi hariç. value = "kind:id".
  const byproductOptions = useMemo<SSOption[]>(
    () => (produciblesQ.data ?? [])
      .filter((p) => !(p.kind === producedKind && p.id === producedId))
      .map((p) => ({ value: `${p.kind}:${p.id}`, label: p.name, tail: p.code })),
    [produciblesQ.data, producedKind, producedId],
  );
  const bypUomSym = (itemKind: ProducibleKind, itemId: string) =>
    (produciblesQ.data ?? []).find((p) => p.kind === itemKind && p.id === itemId)?.baseUomSymbol ?? "";

  // Aranabilir üretim merkezi seçici — aktif uretim_merkezi lokasyonları.
  const centerOptions = useMemo<SSOption[]>(
    () => ((locsQ.data ?? []) as Array<{ id: string; code: string; name: string; type: string; isActive: boolean }>)
      .filter((l) => l.type === "uretim_merkezi" && l.isActive)
      .map((l) => ({ value: l.id, label: l.name, tail: l.code })),
    [locsQ.data],
  );

  /**
   * ⚠ SESSİZLİK YASAĞI — KIRILIM GİZLİYSE FORM AÇILMAZ.
   * Uç, `view:recipe` izni olmayana `items: []` + `detailHidden: true` döner. Bu bayrak
   * okunmazsa editör SIFIR satırla açılır: kullanıcı reçetenin boş olduğunu sanır, kaydetmeye
   * çalışınca "En az 1 geçerli reçete satırı girin" duvarına çarpar ve nedenini ÖĞRENEMEZ.
   * (Uçta `update` gelen `items` ile mevcutları değiştirdiği için boş formla kayıt, izin
   * kapısı olmasa, reçeteyi silerdi.) Sebep AÇIKÇA yazılır.
   */
  const kirilimGizli = (isEdit && existingQ.data?.detailHidden === true) || (isCopy && sourceQ.data?.detailHidden === true);

  const errorMessage = useMemo(() => {
    const mut = createMut.error ?? updateMut.error;
    if (submitErr) return submitErr;
    if (!mut) return null;
    const zod = (mut.data as { zodError?: { fieldErrors?: Record<string, string[]> } } | undefined)?.zodError?.fieldErrors;
    if (zod) return Object.values(zod).flat().filter(Boolean).join(" · ");
    return mut.message;
  }, [submitErr, createMut.error, updateMut.error]);

  // Sabit Miktar (absolute) modunda Batch Boyutu = reçete satırlarının toplamı.
  // Girdi miktarları toplanır → ortaya çıkan ürün miktarını (batch) belirler.
  // Birim tutarlılığı için yalnızca BASKIN birimdeki (en çok kullanılan) satırlar toplanır.
  const autoBatch = useMemo(() => {
    const valid = rows
      .map((r) => ({ qty: Number(r.qty), uomId: r.uomId }))
      .filter((r) => r.uomId && Number.isFinite(r.qty) && r.qty > 0);
    if (valid.length === 0) return null;
    const counts = new Map<string, number>();
    const order: string[] = [];
    for (const r of valid) {
      if (!counts.has(r.uomId)) order.push(r.uomId);
      counts.set(r.uomId, (counts.get(r.uomId) ?? 0) + 1);
    }
    let best = "";
    let bestCount = -1;
    for (const u of order) {
      const c = counts.get(u) ?? 0;
      if (c > bestCount) { bestCount = c; best = u; }
    }
    const sum = valid.filter((r) => r.uomId === best).reduce((s, r) => s + r.qty, 0);
    const excluded = valid.filter((r) => r.uomId !== best).length;
    return { batchSize: Math.round(sum * 10000) / 10000, batchUomId: best, excluded };
  }, [rows]);

  const autoBatchText = autoBatch ? autoBatch.batchSize.toLocaleString("tr-TR", { maximumFractionDigits: 4 }) : "";

  // Üretilen ürünün taban birimi — batch birimi her zaman buna eşit (scale = hedef/batch).
  const producedBaseUom = useMemo(() => {
    // Üretilen ürünün taban birimi — producibles'tan (capsiz, baseUom dahil).
    const item = (produciblesQ.data ?? []).find((p) => p.id === producedId);
    return item?.baseUomId ? { id: item.baseUomId, symbol: item.baseUomSymbol ?? "" } : null;
  }, [produciblesQ.data, producedId]);

  // Kütle dengesi: satırların baskın birimi üretilen ürünün taban birimiyle aynıysa
  // batch = satır toplamı (otomatik). Aksi halde (ör. kg girdi → adet çıktı) elle girilir.
  const isMassBalance = !!(autoBatch && producedBaseUom && autoBatch.batchUomId === producedBaseUom.id);

  // ── Maliyet: kalemlerin birim fiyatı (StockCardPrice) → tutar → toplam reçete maliyeti ──
  const distinctItems = useMemo(() => {
    const seen = new Set<string>();
    const out: Array<{ kind: ItemKind; id: string }> = [];
    for (const r of rows) {
      if (!r.consumedId) continue;
      const k = `${r.consumedKind}:${r.consumedId}`;
      if (seen.has(k)) continue;
      seen.add(k);
      out.push({ kind: r.consumedKind, id: r.consumedId });
    }
    return out;
  }, [rows]);

  const itemCostsQ = trpc.recipe.itemCosts.useQuery(
    { items: distinctItems },
    { enabled: open && distinctItems.length > 0, staleTime: 30_000 },
  );

  const priceMap = useMemo(() => {
    const m = new Map<string, { unitPrice: number | null; currency: string | null }>();
    (itemCostsQ.data ?? []).forEach((r) => m.set(`${r.kind}:${r.id}`, { unitPrice: r.unitPrice, currency: r.currency }));
    return m;
  }, [itemCostsQ.data]);

  const cost = useMemo(() => {
    let missing = 0;
    const curTotals = new Map<string, number>(); // para birimi → toplam tutar
    const curCount = new Map<string, number>();   // para birimi → kalem sayısı
    for (const r of rows) {
      if (!r.consumedId) continue;
      const p = priceMap.get(`${r.consumedKind}:${r.consumedId}`);
      const qty = Number(r.qty);
      if (!p || p.unitPrice == null) { missing++; continue; }
      if (Number.isFinite(qty) && qty > 0) {
        const cur = p.currency ?? "TRY";
        curTotals.set(cur, (curTotals.get(cur) ?? 0) + qty * p.unitPrice);
        curCount.set(cur, (curCount.get(cur) ?? 0) + 1);
      }
    }
    if (curTotals.size === 0) return { total: null, missing, currency: "TRY", mixed: false, excludedCur: 0 };
    // Baskın para birimi = en çok kaleme sahip. Toplam YALNIZCA baskın birim kalemlerini içerir
    // (farklı birimler toplanamaz — karışıksa diğerleri hariç tutulup uyarı gösterilir).
    let currency = "TRY";
    let best = -1;
    for (const [c, n] of curCount) { if (n > best) { best = n; currency = c; } }
    const mixed = curTotals.size > 1;
    const excludedCur = mixed ? [...curCount].filter(([c]) => c !== currency).reduce((s, [, n]) => s + n, 0) : 0;
    return { total: curTotals.get(currency) ?? 0, missing, currency, mixed, excludedCur };
  }, [rows, priceMap]);

  // Çıktı birimi başına maliyet — yalnız Sabit Miktar (absolute) modda anlamlı (toplam ÷ çıktı miktarı).
  const effectiveBatch = isMassBalance && autoBatch ? autoBatch.batchSize : Number(batchSize);
  const perOutput = mode === "absolute" && cost.total != null && Number.isFinite(effectiveBatch) && effectiveBatch > 0
    ? cost.total / effectiveBatch
    : null;
  const outputUomSym = producedBaseUom?.symbol ?? uomQ.data?.find((u) => u.id === batchUomId)?.symbol ?? "";
  const costSym = CUR_SYMBOL[cost.currency] ?? cost.currency;

  function setRow(idx: number, patch: Partial<ItemRow>) {
    setRows((rs) => rs.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  }
  function addRow() {
    setRows((rs) => [...rs, emptyRow(kgUom?.id ?? "")]);
  }
  function removeRow(idx: number) {
    setRows((rs) => rs.filter((_, i) => i !== idx));
  }

  /**
   * Bir item seçilince kind'i VE BİRİMİ set et (option value = "kind:id").
   * ⚠ BİRİM KARTTAN GELİR. Eskiden yalnız kind/id yazılıyordu; satır `emptyRow`'un sabit "kg"
   * varsayılanıyla kalıyordu. "Ezberden kg" hatasının KAYNAĞI buydu — koli/palet/genel gider
   * gibi ADET kalemleri kg olarak kaydediliyordu. Kart birimi bilinmiyorsa mevcut seçim korunur.
   */
  function pickItem(idx: number, value: string) {
    const [kind, id] = value.split(":") as [ItemKind, string];
    const birim = kartBirimi.get(id);
    setRow(idx, { consumedKind: kind, consumedId: id, ...(birim ? { uomId: birim } : {}) });
  }

  // Yan ürün satırı yönetimi (co-production)
  function setByp(idx: number, patch: Partial<BypRow>) {
    setByproducts((bs) => bs.map((b, i) => (i === idx ? { ...b, ...patch } : b)));
  }
  function addByp() { setByproducts((bs) => [...bs, { itemKind: "semi", itemId: "", qty: "" }]); }
  function removeByp(idx: number) { setByproducts((bs) => bs.filter((_, i) => i !== idx)); }
  function pickByp(idx: number, value: string) {
    const [kind, id] = value.split(":") as [ProducibleKind, string];
    setByp(idx, { itemKind: kind, itemId: id });
  }

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setSubmitErr(null);
    if (!isEdit && !producedId) return setSubmitErr("Üretilen ürün seçin.");
    if (tp.show && !tesisPick) return setSubmitErr("Tesis seçin (Global dahil).");
    const validRows = rows.filter((r) => r.consumedId && r.qty && Number(r.qty) > 0 && r.uomId);
    if (validRows.length === 0) return setSubmitErr("En az 1 geçerli reçete satırı girin.");

    // Batch: absolute modda satır toplamından (autoBatch) türetilir; percentage modda manuel.
    let finalBatchSize: number;
    let finalBatchUomId: string;
    if (mode === "absolute") {
      finalBatchUomId = producedBaseUom?.id ?? batchUomId;
      if (!finalBatchUomId) return setSubmitErr("Üretilen ürünün birimi belirlenemedi.");
      if (isMassBalance && autoBatch) {
        finalBatchSize = autoBatch.batchSize;
      } else {
        if (!batchSize || Number(batchSize) <= 0) return setSubmitErr("Geçerli batch boyutu girin.");
        finalBatchSize = Number(batchSize);
      }
    } else {
      if (!batchSize || Number(batchSize) <= 0) return setSubmitErr("Geçerli batch boyutu girin.");
      if (!batchUomId) return setSubmitErr("Batch birimi seçin.");
      finalBatchSize = Number(batchSize);
      finalBatchUomId = batchUomId;
    }

    const items = validRows.map((r) => ({
      consumedKind: r.consumedKind,
      consumedId: r.consumedId,
      qty: Number(r.qty),
      uomId: r.uomId,
      scrapPct: Number(r.scrapPct) || 0,
      consumedInConversion: r.consumedInConversion,
    }));

    // Yan ürünler (co-production): boş satır atlanır; kısmi dolu satır hata. Boş dizi de gönderilir (update temizler).
    const byps: Array<{ itemKind: ProducibleKind; itemId: string; qty: number }> = [];
    for (const b of byproducts) {
      const q = Number(b.qty);
      if (!b.itemId && !b.qty) continue;
      if (!b.itemId) return setSubmitErr("Yan ürün seçin veya satırı kaldırın.");
      if (!(q > 0)) return setSubmitErr("Her yan ürün için 0'dan büyük miktar girin.");
      byps.push({ itemKind: b.itemKind, itemId: b.itemId, qty: q });
    }

    try {
      if (isEdit && recipeId) {
        await updateMut.mutateAsync({
          id: recipeId,
          mode, batchSize: finalBatchSize, batchUomId: finalBatchUomId,
          expectedYieldPct: Number(yieldPct) || 100,
          notes: notes || null,
          productionCenterId: productionCenterId || null,
          items,
          byproducts: byps,
        });
      } else {
        await createMut.mutateAsync({
          producedKind, producedId,
          mode, batchSize: finalBatchSize, batchUomId: finalBatchUomId,
          expectedYieldPct: Number(yieldPct) || 100,
          notes: notes || undefined,
          productionCenterId: productionCenterId || null,
          items,
          byproducts: byps.length ? byps : undefined,
          tesisId: resolveTesisId(tp.show, tesisPick),
        });
      }
      await qc.invalidateQueries();
      onClose();
    } catch {
      /* err banner */
    }
  }

  const modeLabel = mode === "percentage" ? "%" : "miktar";
  // Reçete satırı grid'i — başlık satırı ile birebir aynı (Miktar %50 geniş: 70→105). +Dön. (dönüşümde tükenir).
  const rowGrid = "minmax(0, 1fr) 105px 56px 48px 40px 96px 26px";
  // Yan ürün satırı grid'i: [ürün | miktar | birim | sil].
  const bypGrid = "minmax(0, 1fr) 105px 56px 26px";

  return (
    <Drawer
      open={open}
      onClose={onClose}
      size="3xl"
      title={isEdit ? "Reçete Düzenle" : isCopy ? "Reçete Kopyala" : "Yeni Reçete"}
      subtitle={
        isEdit && existingQ.data
          ? `${existingQ.data.producedCode} · v${existingQ.data.version} · ${existingQ.data.status}`
          : isCopy && sourceQ.data
            ? `Kaynak: ${sourceQ.data.producedCode} v${sourceQ.data.version} — hedef ürünü seçip içeriği revize edin`
            : "Üretilen ürün ve tüketilen malzemeler"
      }
      footer={
        <>
          <button type="button" className="btn-md ghost" onClick={onClose} disabled={isPending}>İptal</button>
          <button
            type="submit"
            form="recipe-form"
            className="btn-md primary"
            disabled={isPending || kirilimGizli}
            title={kirilimGizli ? "Reçete kırılımı size kapalı — içerik yüklenemediği için kaydetmek reçeteyi bozardı" : undefined}
          >
            {isPending ? "Kaydediliyor…" : isEdit ? "Güncelle" : "Oluştur (taslak)"}
          </button>
        </>
      }
    >
      <form id="recipe-form" onSubmit={handleSubmit} noValidate>
        {kirilimGizli ? (
          <div className="form-banner-err">
            Bu reçetenin kırılımı size <b>kapalı</b>, bu yüzden içeriği yüklenemedi — form boş görünüyor.
            Kaydetmeyin. Görmek/düzenlemek için <b>“Reçete kırılımını görüntüle”</b> (<code>view:recipe</code>)
            izni gerekir; Ayarlar › Kullanıcı Grupları'ndan verilebilir.
          </div>
        ) : null}
        {errorMessage ? <div className="form-banner-err">{errorMessage}</div> : null}
        <TesisPickerField show={tp.show} facilities={tp.facilities} value={tesisPick} onChange={setTesisPick} />

        {/* Üretilen ürün */}
        <div className="form-row">
          <div className="form-field">
            <label className="lbl" htmlFor="re-kind">Üretilen Tür <span className="req">*</span></label>
            <select id="re-kind" className="form-select" value={producedKind} onChange={(e) => { setProducedKind(e.target.value as ProducibleKind); setProducedId(""); }} disabled={isEdit}>
              <option value="finished">Mamul</option>
              <option value="semi">Yarı Mamul</option>
            </select>
          </div>
          <div className="form-field">
            <label className="lbl" htmlFor="re-product">Üretilen Ürün <span className="req">*</span></label>
            <SearchSelect
              id="re-product"
              value={producedId || null}
              options={produciblesOptions}
              onChange={(v) => setProducedId(v ?? "")}
              placeholder="— Seçin —"
              searchPlaceholder="Ürün adı / kodu ara…"
              emptyText="Ürün bulunamadı"
              disabled={isEdit}
            />
            {isEdit ? <span className="help">Yarat sonrası değiştirilemez.</span> : null}
          </div>
        </div>

        <div className="form-field">
          <label className="lbl" htmlFor="re-center">Üretim Merkezi</label>
          <SearchSelect
            id="re-center"
            value={productionCenterId || null}
            options={centerOptions}
            onChange={(v) => setProductionCenterId(v ?? "")}
            placeholder="— Merkez seç (opsiyonel) —"
            searchPlaceholder="Merkez adı / kodu ara…"
            emptyText="Üretim merkezi tanımlı değil"
          />
          <span className="help">Bu reçete hangi üretim merkezinde üretilecek? Hat, üretim anında seçilir.</span>
        </div>

        {/* Mode + batch + yield */}
        <div className="form-field">
          <span className="lbl">Miktar Modu</span>
          <div className="form-radio-group" role="radiogroup">
            <label className="form-radio">
              <input type="radio" name="mode" checked={mode === "absolute"} onChange={() => setMode("absolute")} />
              <span>Sabit Miktar</span>
            </label>
            <label className="form-radio">
              <input type="radio" name="mode" checked={mode === "percentage"} onChange={() => setMode("percentage")} />
              <span>Yüzde (%)</span>
            </label>
          </div>
          <span className="help">{mode === "absolute" ? "Satırlar mutlak miktar; batch boyutuna oranla ölçeklenir." : "Satırlar batch'in yüzdesi (baker's %)."}</span>
        </div>

        <div className="form-row">
          <div className="form-field">
            <label className="lbl" htmlFor="re-batch">
              Batch Boyutu {mode === "absolute" && isMassBalance ? <span className="help" style={{ fontWeight: 400 }}>(otomatik)</span> : <span className="req">*</span>}
            </label>
            {mode === "absolute" ? (
              <>
                <div style={{ display: "flex", gap: 6 }}>
                  {isMassBalance ? (
                    <input
                      id="re-batch"
                      className="form-input"
                      type="text"
                      readOnly
                      tabIndex={-1}
                      value={autoBatchText || "—"}
                      aria-readonly="true"
                      style={{ flex: 2, background: "var(--neutral-100)", color: "var(--neutral-700)", fontFamily: "var(--font-mono)", fontVariantNumeric: "tabular-nums", cursor: "default" }}
                    />
                  ) : (
                    <input id="re-batch" className="form-input" type="number" step="any" min={0} value={batchSize} onChange={(e) => setBatchSize(e.target.value)} inputMode="decimal" required style={{ flex: 2 }} />
                  )}
                  <input
                    className="form-input"
                    type="text"
                    readOnly
                    tabIndex={-1}
                    value={producedBaseUom?.symbol ?? ""}
                    aria-label="Batch birimi"
                    style={{ flex: 1, background: "var(--neutral-100)", color: "var(--neutral-700)", textAlign: "center", cursor: "default" }}
                  />
                </div>
                <span className="help">
                  {isMassBalance
                    ? `Reçete satırlarının toplamı — verilen miktarlar bu kadar ürün üretir.${autoBatch && autoBatch.excluded > 0 ? ` (${autoBatch.excluded} satır farklı birimde, toplama dahil değil)` : ""}`
                    : producedBaseUom
                      ? `Üretilen ürün birimi (${producedBaseUom.symbol}) reçete satırı birim(ler)inden farklı — bir çalıştırmada üretilen miktarı elle girin.`
                      : "Bu reçete bir çalıştırmada kaç birim üretir."}
                </span>
              </>
            ) : (
              <>
                <div style={{ display: "flex", gap: 6 }}>
                  <input id="re-batch" className="form-input" type="number" step="any" min={0} value={batchSize} onChange={(e) => setBatchSize(e.target.value)} inputMode="decimal" required style={{ flex: 2 }} />
                  <select className="form-select" value={batchUomId} onChange={(e) => setBatchUomId(e.target.value)} style={{ flex: 1 }} required>
                    <option value="">birim</option>
                    {uomQ.data?.map((u) => <option key={u.id} value={u.id}>{u.symbol}</option>)}
                  </select>
                </div>
                <span className="help">% baz alınan referans miktar (satırlar bunun yüzdesidir).</span>
              </>
            )}
          </div>
          <div className="form-field">
            <label className="lbl" htmlFor="re-yield">Beklenen Verim (%)</label>
            <input id="re-yield" className="form-input" type="number" step="any" min={0} value={yieldPct} onChange={(e) => setYieldPct(e.target.value)} inputMode="decimal" />
            <span className="help">Girdi → çıktı oranı (örn. 92 = nem kaybı).</span>
          </div>
        </div>

        {/* Reçete satırları */}
        <div style={{ marginTop: 8, marginBottom: 6, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <span className="lbl" style={{ fontSize: 13, fontWeight: 600 }}>Reçete Satırları (tüketilen)</span>
          <button type="button" className="btn-md secondary" style={{ height: 30, fontSize: 12.5 }} onClick={addRow}><Icon name="plus" size={13} />Satır</button>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {rows.length > 0 ? (
            <div style={{
              display: "grid",
              gridTemplateColumns: rowGrid,
              gap: 6,
              padding: "0 9px",
              fontSize: 10.5,
              fontWeight: 600,
              letterSpacing: "0.02em",
              textTransform: "uppercase",
              color: "var(--neutral-500)",
            }}>
              <span>Malzeme</span>
              <span>Miktar</span>
              <span>Birim</span>
              <span title="Bu satıra özgü ek fire/zayiat yüzdesi — tüketilen miktarı bu oranda artırır">Fire %</span>
              <span title="Ürün dönüşümünde bu kalem de tüketilir (dönüşüm anında onayla)" style={{ textAlign: "center" }}>Dön.</span>
              <span style={{ textAlign: "right" }}>Tutar</span>
              <span aria-hidden="true" />
            </div>
          ) : null}
          {rows.map((row, idx) => {
            const price = row.consumedId ? priceMap.get(`${row.consumedKind}:${row.consumedId}`) : undefined;
            const hasPrice = !!price && price.unitPrice != null;
            const sym = CUR_SYMBOL[price?.currency ?? "TRY"] ?? (price?.currency ?? "");
            const qtyN = Number(row.qty);
            const lineTotal = hasPrice && Number.isFinite(qtyN) && qtyN > 0 ? qtyN * (price!.unitPrice as number) : null;
            return (
            <div key={idx} style={{
              display: "grid",
              gridTemplateColumns: rowGrid,
              gap: 6,
              alignItems: "center",
              padding: 8,
              border: "1px solid var(--neutral-200)",
              borderRadius: "var(--radius-md)",
              background: "var(--neutral-50)",
            }}>
              <SearchSelect
                id={`re-item-${idx}`}
                value={row.consumedId ? `${row.consumedKind}:${row.consumedId}` : null}
                options={ingredientGroups}
                onChange={(v) => (v ? pickItem(idx, v) : setRow(idx, { consumedId: "" }))}
                placeholder="— Malzeme seç —"
                searchPlaceholder="Malzeme adı / kodu ara…"
                emptyText="Malzeme bulunamadı"
              />
              <input
                className="form-input"
                type="number"
                step="any"
                min={0}
                placeholder={modeLabel}
                value={row.qty}
                onChange={(e) => setRow(idx, { qty: e.target.value })}
                style={{ height: 32, fontSize: 12.5 }}
                inputMode="decimal"
                aria-label="Miktar"
              />
              <select
                className="form-select"
                value={row.uomId}
                onChange={(e) => setRow(idx, { uomId: e.target.value })}
                style={{ height: 32, fontSize: 12.5 }}
                aria-label="Birim"
              >
                {uomQ.data?.map((u) => <option key={u.id} value={u.id}>{u.symbol}</option>)}
              </select>
              <input
                className="form-input"
                type="number"
                step="any"
                min={0}
                placeholder="fire%"
                value={row.scrapPct}
                onChange={(e) => setRow(idx, { scrapPct: e.target.value })}
                style={{ height: 32, fontSize: 12.5 }}
                inputMode="decimal"
                aria-label="Fire yüzdesi"
                title="Bu satıra özgü ek fire %"
              />
              {/* Dönüşümde tükenir mi (ürün dönüşümü) */}
              <div style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                <input
                  type="checkbox"
                  checked={row.consumedInConversion}
                  onChange={(e) => setRow(idx, { consumedInConversion: e.target.checked })}
                  aria-label="Dönüşümde tükenir"
                  title="Ürün dönüşümünde bu kalem de tüketilir"
                  style={{ width: 15, height: 15, cursor: "pointer", accentColor: "var(--primary)" }}
                />
              </div>
              {/* Tutar (miktar × birim fiyat) + alt satırda birim fiyat */}
              <div style={{ textAlign: "right", lineHeight: 1.15, overflow: "hidden" }} aria-label="Tutar">
                {hasPrice ? (
                  <>
                    <div style={{ fontFamily: "var(--font-mono)", fontVariantNumeric: "tabular-nums", fontSize: 12.5, color: "var(--neutral-900)", whiteSpace: "nowrap" }}>
                      {lineTotal != null ? `${fmtMoney(lineTotal)} ${sym}` : "—"}
                    </div>
                    <div style={{ fontSize: 10.5, color: "var(--neutral-500)", whiteSpace: "nowrap" }} title="Birim fiyat (Alış Fiyatı)">
                      {fmtMoney(price!.unitPrice as number)} {sym}/br
                    </div>
                  </>
                ) : row.consumedId ? (
                  <span style={{ fontSize: 10.5, color: "var(--neutral-400)" }} title="Bu kart için Alış Fiyatı tanımlı değil">fiyat yok</span>
                ) : (
                  <span style={{ color: "var(--neutral-300)" }}>—</span>
                )}
              </div>
              <button
                type="button"
                onClick={() => removeRow(idx)}
                aria-label="Satırı kaldır"
                style={{ height: 28, width: 26, border: 0, background: "transparent", color: "var(--danger)", cursor: "pointer", borderRadius: 4, display: "inline-flex", alignItems: "center", justifyContent: "center" }}
                title="Kaldır"
              ><Icon name="x" size={15} /></button>
            </div>
            );
          })}
          {rows.length === 0 ? (
            <div className="d-card">
              <div className="d-empty">
                <Icon name="list" size={24} />
                <p className="m">Henüz satır yok. "+ Satır" ile ekleyin.</p>
              </div>
            </div>
          ) : null}
        </div>

        {/* Toplam reçete maliyeti */}
        {rows.some((r) => r.consumedId) ? (
          <div style={{
            display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 12,
            marginTop: 12, padding: "10px 12px",
            background: "var(--neutral-50)", border: "1px solid var(--neutral-200)", borderRadius: "var(--radius-md)",
          }}>
            <span style={{ fontSize: 12.5, fontWeight: 600, color: "var(--neutral-700)" }}>
              Toplam Reçete Maliyeti
              <span style={{ fontWeight: 400, color: "var(--neutral-400)", marginLeft: 6, fontSize: 11 }}>(Alış Fiyatı bazlı)</span>
              {itemCostsQ.isFetching ? <span style={{ fontWeight: 400, color: "var(--neutral-400)", marginLeft: 6 }}>hesaplanıyor…</span> : null}
            </span>
            <span style={{ display: "flex", alignItems: "baseline", gap: 10 }}>
              {perOutput != null ? (
                <span style={{ fontSize: 11.5, color: "var(--neutral-500)", fontFamily: "var(--font-mono)", fontVariantNumeric: "tabular-nums" }}>
                  {fmtMoney(perOutput)} {costSym}{outputUomSym ? `/${outputUomSym}` : ""}
                </span>
              ) : null}
              <span style={{ fontFamily: "var(--font-mono)", fontVariantNumeric: "tabular-nums", fontSize: 15, fontWeight: 700, color: "var(--neutral-900)" }}>
                {cost.total != null ? `${fmtMoney(cost.total)} ${costSym}` : "—"}
              </span>
            </span>
          </div>
        ) : null}
        {cost.missing > 0 ? (
          <div style={{ fontSize: 11, color: "var(--warning)", marginTop: 4 }}>
            {cost.missing} kalemde birim fiyat (Alış Fiyatı) tanımlı değil — toplama dahil edilmedi.
          </div>
        ) : null}
        {cost.mixed ? (
          <div style={{ fontSize: 11, color: "var(--warning)", marginTop: 4 }}>
            Kalemler farklı para birimlerinde — toplam yalnızca baskın birim ({costSym}) kalemlerini içerir
            {cost.excludedCur > 0 ? ` (${cost.excludedCur} kalem hariç)` : ""}.
          </div>
        ) : null}

        {/* Yan Ürünler (co-production) — bu reçete üretilince reçetedeki miktara göre otomatik üretilir. */}
        <div style={{ marginTop: 16, marginBottom: 6, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <span className="lbl" style={{ fontSize: 13, fontWeight: 600 }}>
            Yan Ürünler <span style={{ fontWeight: 400, color: "var(--neutral-400)", fontSize: 11 }}>(üretimde otomatik üretilir)</span>
          </span>
          <button type="button" className="btn-md secondary" style={{ height: 30, fontSize: 12.5 }} onClick={addByp} disabled={!isEdit && !producedId}>
            <Icon name="plus" size={13} />Yan Ürün
          </button>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {byproducts.length > 0 ? (
            <div style={{
              display: "grid", gridTemplateColumns: bypGrid, gap: 6, padding: "0 9px",
              fontSize: 10.5, fontWeight: 600, letterSpacing: "0.02em", textTransform: "uppercase", color: "var(--neutral-500)",
            }}>
              <span>Yan Ürün</span>
              <span>Miktar {mode === "percentage" ? "(%)" : "(batch başına)"}</span>
              <span>Birim</span>
              <span aria-hidden="true" />
            </div>
          ) : null}
          {byproducts.map((b, idx) => (
            <div key={idx} style={{
              display: "grid", gridTemplateColumns: bypGrid, gap: 6, alignItems: "center", padding: 8,
              border: "1px solid var(--neutral-200)", borderRadius: "var(--radius-md)", background: "var(--neutral-50)",
            }}>
              <SearchSelect
                id={`re-byp-${idx}`}
                value={b.itemId ? `${b.itemKind}:${b.itemId}` : null}
                options={byproductOptions}
                onChange={(v) => (v ? pickByp(idx, v) : setByp(idx, { itemId: "" }))}
                placeholder="— Yan ürün seç —"
                searchPlaceholder="Ürün adı / kodu ara…"
                emptyText="Ürün bulunamadı"
              />
              <input
                className="form-input"
                type="number"
                step="any"
                min={0}
                placeholder={mode === "percentage" ? "%" : "miktar"}
                value={b.qty}
                onChange={(e) => setByp(idx, { qty: e.target.value })}
                style={{ height: 32, fontSize: 12.5 }}
                inputMode="decimal"
                aria-label="Yan ürün miktarı"
              />
              <input
                className="form-input"
                type="text"
                readOnly
                tabIndex={-1}
                value={b.itemId ? bypUomSym(b.itemKind, b.itemId) : ""}
                aria-label="Birim"
                style={{ height: 32, fontSize: 12.5, background: "var(--neutral-100)", color: "var(--neutral-700)", textAlign: "center", cursor: "default" }}
              />
              <button
                type="button"
                onClick={() => removeByp(idx)}
                aria-label="Yan ürünü kaldır"
                style={{ height: 28, width: 26, border: 0, background: "transparent", color: "var(--danger)", cursor: "pointer", borderRadius: 4, display: "inline-flex", alignItems: "center", justifyContent: "center" }}
                title="Kaldır"
              ><Icon name="x" size={15} /></button>
            </div>
          ))}
          {byproducts.length > 0 ? (
            <span className="help">Her yan ürün KENDİ reçetesine göre bileşen tüketir; miktar üretilen miktara oranla ölçeklenir.</span>
          ) : null}
        </div>

        <div className="form-field" style={{ marginTop: 14 }}>
          <label className="lbl" htmlFor="re-notes">Notlar</label>
          <textarea id="re-notes" className="form-textarea" value={notes} onChange={(e) => setNotes(e.target.value)} maxLength={1000} rows={2} />
        </div>

        <div style={{ fontSize: 11.5, color: "var(--neutral-500)", marginTop: 4 }}>
          Yeni reçete <b>taslak</b> olarak kaydedilir. Listeden "Yayına Al" ile aktifleştirin (önceki aktif versiyon otomatik arşivlenir).
        </div>
      </form>
    </Drawer>
  );
}
